import { useCallback, useRef, useEffect } from 'react';
import {
    fetchPins as _fetchPins,
    createPin as _createPin,
    updatePin as _updatePin,
    deletePin as _deletePin,
} from '../../api/pinService';

import { formatPin } from '../../../../utils/pinFormat';
import { useToastContext } from '../../../../App';
import { retryApiCall } from '../../../../utils/retryUtils';

export const usePinActions = (projectId, { setPins, setCurrentPin }) => {
    const { showToast } = useToastContext();
    const operationInProgress = useRef(new Set());
    const setPinsRef = useRef(setPins);

    // setPinsの参照を常に最新に保つ
    useEffect(() => {
        setPinsRef.current = setPins;
    }, [setPins]);

    if (!projectId) {
        throw new Error("usePinActions requires a valid projectId");
    }

    const refreshPins = useCallback(async () => {
        try {
            const data = await retryApiCall(() => _fetchPins(projectId), {
                maxRetries: 2,
                baseDelay: 500
            });
            const formattedPins = data.map(formatPin);
            setPinsRef.current(formattedPins);
        } catch (error) {
            console.error("Error fetching pins:", error);
        }
    }, [projectId]);

    const savePin = async (pin) => {
        if (!pin || !pin.title?.trim()) return null;

        // 重複リクエスト防止
        const operationKey = `save_${pin.id || 'new'}`;
        if (operationInProgress.current.has(operationKey)) {
            console.warn('Pin save operation already in progress, skipping duplicate request');
            return null;
        }

        operationInProgress.current.add(operationKey);

        const payload = {
            title: pin.title,
            description: pin.description,
            latitude: pin.position.lat,
            longitude: pin.position.lng,
            label: pin.labelId || null, // ラベルが未選択の場合はnullを送信（バックエンドで「ラベルなし」が自動設定される）
        };

        if (pin.thumbnail instanceof File) {
            payload.thumbnail = pin.thumbnail;
        } else if (pin.thumbnail === null) {
            payload.thumbnail = null;
        }

        try {
            const response = pin.id
                ? await retryApiCall(() => _updatePin(projectId, pin.id, payload), {
                    maxRetries: 2,
                    baseDelay: 500
                })
                : await retryApiCall(() => _createPin(projectId, payload), {
                    maxRetries: 2,
                    baseDelay: 500
                });

            const formatted = formatPin(response.data);

            // 楽観的更新: 即座にUIを更新
            setPinsRef.current(prevPins => {
                if (pin.id) {
                    // 更新の場合
                    return prevPins.map(p => p.id === pin.id ? formatted : p);
                } else {
                    // 新規作成の場合
                    return [...prevPins, formatted];
                }
            });

            // 成功メッセージを表示
            const action = pin.id ? "更新" : "保存";
            showToast(`ピンを${action}しました`, "success");

            return formatted;
        } catch (error) {
            console.error('Failed to save pin:', error);
            console.error('Error response:', error.response);
            console.error('Error response data:', error.response?.data);
            console.error('Error response status:', error.response?.status);
            console.error('Error message:', error.message);

            // エラー時は最新データを取得して状態を同期
            try {
                await refreshPins();
            } catch (refreshError) {
                console.warn('Failed to refresh pins after error:', refreshError);
            }

            let errorMessage = "保存に失敗しました。";

            if (error?.response?.status === 413) {
                errorMessage = "ファイルサイズが大きすぎます。容量制限を確認してください。";
            } else if (error?.response?.status === 400) {
                const responseData = error.response?.data;

                if (responseData?.error) {
                    errorMessage = responseData.message || responseData.error;
                } else if (responseData?.detail) {
                    errorMessage = responseData.detail;
                } else if (responseData?.non_field_errors) {
                    errorMessage = responseData.non_field_errors.join(', ');
                } else if (typeof responseData === 'object') {
                    const fieldErrors = Object.values(responseData)
                        .filter(Array.isArray)
                        .flat();
                    if (fieldErrors.length > 0) {
                        errorMessage = fieldErrors.join(', ');
                    } else {
                        const errorValues = Object.values(responseData)
                            .filter(value => typeof value === 'string')
                            .join(', ');
                        if (errorValues) {
                            errorMessage = errorValues;
                        }
                    }
                }
            } else if (error?.response?.status === 500) {
                errorMessage = "サーバーエラーが発生しました。しばらく時間をおいて再度お試しください。";
            } else if (error?.response?.status === 403) {
                errorMessage = "権限がありません。";
            } else if (error?.response?.status === 404) {
                errorMessage = "リソースが見つかりません。";
            } else if (error?.message) {
                errorMessage = error.message;
            }

            // エラーメッセージを表示
            showToast(errorMessage, "error");

            const enhancedError = new Error(errorMessage);
            enhancedError.originalError = error;
            enhancedError.status = error?.response?.status;
            enhancedError.response = error?.response;

            throw enhancedError;
        } finally {
            // 操作完了フラグをクリア
            operationInProgress.current.delete(operationKey);
        }
    };

    const deletePin = async (id) => {
        if (!id) return false;

        // 重複リクエスト防止
        const operationKey = `delete_${id}`;
        if (operationInProgress.current.has(operationKey)) {
            console.warn('Pin delete operation already in progress, skipping duplicate request');
            return false;
        }

        operationInProgress.current.add(operationKey);

        try {
            // 楽観的更新: 即座にUIから削除
            setPinsRef.current(prevPins => prevPins.filter(pin => pin.id !== id));
            setCurrentPin(null);

            await retryApiCall(() => _deletePin(projectId, id), {
                maxRetries: 2,
                baseDelay: 500
            });

            // 成功メッセージを表示
            showToast("ピンを削除しました", "success");
            return true;
        }
        catch (error) {
            console.error("Failed to delete pin:", error);

            // エラー時は最新データを取得して状態を同期
            try {
                await refreshPins();
            } catch (refreshError) {
                console.warn('Failed to refresh pins after delete error:', refreshError);
            }

            let errorMessage = "削除に失敗しました。";

            if (error?.response?.status === 403) {
                errorMessage = "削除する権限がありません。";
            } else if (error?.response?.status === 404) {
                errorMessage = "削除対象が見つかりません。";
            } else if (error?.response?.status === 500) {
                errorMessage = "サーバーエラーが発生しました。しばらく時間をおいて再度お試しください。";
            } else if (error?.message) {
                errorMessage = error.message;
            }

            // エラーメッセージを表示
            showToast(errorMessage, "error");

            const enhancedError = new Error(errorMessage);
            enhancedError.originalError = error;
            enhancedError.status = error?.response?.status;

            throw enhancedError;
        } finally {
            // 操作完了フラグをクリア
            operationInProgress.current.delete(operationKey);
        }
    };

    return { refreshPins, savePin, deletePin };
};